// AdminDashboard.jsx
import React, { useEffect, useState } from "react";
import { auth, db } from "./firebase";
import { doc, getDoc, getDocs, collection, updateDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { Shield, LogOut, Stethoscope, X, Plus } from "lucide-react";
import { signOut } from "firebase/auth";

const viewPrice = (v, c = "EUR") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: c }).format(Number(v || 0));

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingRole, setCheckingRole] = useState(true);
  const [doctorsById, setDoctorsById] = useState({});
  const [activeDoctor, setActiveDoctor] = useState(null);
  const [activeServices, setActiveServices] = useState([]);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const navigate = useNavigate();

  /* === Auth check === */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (current) => {
      if (!current) return navigate("/login");
      setUser(current);
      const u = await getDoc(doc(db, "users", current.uid));
      if (u.exists() && u.data().role === "admin") setIsAdmin(true);
      else navigate("/unauthorized");
      setCheckingRole(false);
    });
    return () => unsub();
  }, [navigate]);

  /* === Load doctors === */
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      const snap = await getDocs(collection(db, "doctors"));
      const map = {};
      snap.forEach((d) => (map[d.id] = { id: d.id, ...d.data() }));
      setDoctorsById(map);
    })();
  }, [isAdmin]);

  async function openDoctorServices(doctor) {
    setActiveDoctor(doctor);
    const docRef = doc(db, "doctors", doctor.id);
    const snap = await getDoc(docRef);
    setActiveServices(snap.exists() ? snap.data().services || [] : []);
    setServiceModalOpen(true);
  }

  async function saveServiceKeywords(doctorId, index, newKeywords) {
    const docRef = doc(db, "doctors", doctorId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return;
    const data = snap.data();
    const updated = [...(data.services || [])];
    updated[index] = { ...updated[index], keywords: newKeywords };
    await updateDoc(docRef, { services: updated });
    setActiveServices(updated);
    setDoctorsById((prev) => ({
      ...prev,
      [doctorId]: { ...prev[doctorId], services: updated },
    }));
  }

  if (checkingRole)
    return <div style={{ display: "grid", placeItems: "center", minHeight: "100vh" }}>Checking access…</div>;
  if (!isAdmin) return null;

  /* =======================================================
     RENDER
  ======================================================= */
  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb", padding: 24 }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
          <h1 style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 800, fontSize: 28 }}>
            <Shield color="#3b82f6" size={28} /> Admin Dashboard
          </h1>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => navigate("/admin-servicemapper")}
              style={{
                background: "#1d4ed8",
                color: "#fff",
                padding: "10px 14px",
                border: "1px solid #1d4ed8",
                borderRadius: 10,
                fontWeight: 800,
              }}
            >
              <Stethoscope size={16} /> Service Mapper
            </button>
            <button
              onClick={() => {
                signOut(auth);
                navigate("/login");
              }}
              style={{
                background: "#ef4444",
                color: "#fff",
                padding: "10px 14px",
                border: "1px solid #ef4444",
                borderRadius: 10,
                fontWeight: 800,
              }}
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>

        {/* Doctors */}
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, padding: 20 }}>
          <h2 style={{ fontWeight: 800 }}>Doctors ({Object.keys(doctorsById).length})</h2>
          {Object.values(doctorsById).map((d) => (
            <div
              key={d.id}
              style={{
                border: "1px solid #f3f4f6",
                borderRadius: 10,
                padding: 12,
                marginBottom: 10,
                background: "#fafafa",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div
                  onClick={() => openDoctorServices(d)}
                  style={{ cursor: "pointer", color: "#1d4ed8", fontWeight: 700 }}
                >
                  {d.name || d.profile?.name || "Doctor"}
                </div>
                <div style={{ color: "#6b7280", fontSize: 13 }}>
                  {d.specialty || d.profile?.specialty || "—"}
                </div>
              </div>
              <span style={{ fontWeight: 700, color: d.services?.length ? "#16a34a" : "#dc2626" }}>
                {d.services?.length ? `✅ ${d.services.length} services` : "❌ No services"}
              </span>
            </div>
          ))}
        </div>

        {/* Modal for editing services */}
        {serviceModalOpen && activeDoctor && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,.4)",
              display: "grid",
              placeItems: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 20,
                padding: 24,
                width: "min(650px,95vw)",
                maxHeight: "90vh",
                overflowY: "auto",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontWeight: 800 }}>
                  {activeDoctor.name || activeDoctor.profile?.name} – Services
                </h2>
                <button
                  onClick={() => setServiceModalOpen(false)}
                  style={{ border: "none", background: "transparent", fontSize: 20, cursor: "pointer" }}
                >
                  ✕
                </button>
              </div>

              {activeServices.length === 0 ? (
                <div style={{ color: "#6b7280" }}>No services.</div>
              ) : (
                <div style={{ display: "grid", gap: 16 }}>
                  {activeServices.map((srv, i) => (
                    <KeywordEditor
                      key={i}
                      srv={srv}
                      index={i}
                      doctorId={activeDoctor.id}
                      onSave={saveServiceKeywords}
                      viewPrice={viewPrice}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ==========================================================
   Tag-based keyword editor
========================================================== */
function KeywordEditor({ srv, index, doctorId, onSave, viewPrice }) {
  const [tags, setTags] = useState(srv.keywords || []);
  const [input, setInput] = useState("");

  const addTag = (value) => {
    const val = value.trim();
    if (!val || tags.includes(val)) return;
    const updated = [...tags, val];
    setTags(updated);
    onSave(doctorId, index, updated);
    setInput("");
  };

  const removeTag = (val) => {
    const updated = tags.filter((t) => t !== val);
    setTags(updated);
    onSave(doctorId, index, updated);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(input);
    }
  };

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 14 }}>
      <div style={{ fontWeight: 800 }}>{srv.title || "Untitled Service"}</div>
      <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 6 }}>
        {srv.price != null ? viewPrice(srv.price, srv.currency) : "—"}
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          marginBottom: 8,
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          padding: "8px",
        }}
      >
        {tags.map((t) => (
          <span
            key={t}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: "#e0f2fe",
              color: "#0369a1",
              padding: "4px 8px",
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {t}
            <X
              size={12}
              onClick={() => removeTag(t)}
              style={{ cursor: "pointer", color: "#0c4a6e" }}
            />
          </span>
        ))}
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type & press Enter…"
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            fontSize: 13,
            minWidth: 80,
          }}
        />
        <button
          onClick={() => addTag(input)}
          style={{
            background: "#1d4ed8",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "4px 8px",
            fontSize: 12,
            cursor: "pointer",
            fontWeight: 700,
          }}
        >
          <Plus size={12} /> Add
        </button>
      </div>

      <div style={{ fontSize: 12, color: tags.length ? "#16a34a" : "#dc2626" }}>
        {tags.length ? "✅ Mapped" : "❌ Missing"}
      </div>
    </div>
  );
}
